import React, { useState, useEffect } from 'react';
import { Mail, Save, Eye, Edit3, ChevronRight, AlertCircle, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const TEMPLATE_INFO = {
  welcome: { name: 'Welcome Email', description: 'Sent when user registers', icon: '👋' },
  verification: { name: 'Email Verification', description: 'Verify email address', icon: '✉️' },
  password_reset: { name: 'Password Reset', description: 'Reset password link', icon: '🔑' },
  premium_activated: { name: 'Premium Activated', description: 'When user upgrades', icon: '👑' },
  new_interest: { name: 'New Interest', description: 'Someone showed interest', icon: '💕' },
  new_message: { name: 'New Message', description: 'New message notification', icon: '💬' },
  account_approved: { name: 'Account Approved', description: 'Admin approved account', icon: '✅' },
  id_verified: { name: 'ID Verified', description: 'Identity verification done', icon: '🪪' },
};

const AdminEmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({ subject: '', body: '', is_active: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/extended/email-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = (templateId) => {
    const template = templates.find(t => t.template_id === templateId);
    setSelectedTemplate(templateId);
    setFormData({
      subject: template?.subject || '',
      body: template?.body || '',
      is_active: template?.is_active ?? true
    });
    setPreview(false);
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/extended/email-templates/${selectedTemplate}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ template_id: selectedTemplate, ...formData })
      });

      if (response.ok) {
        toast({ title: "Template Saved" });
        loadTemplates();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mint-500" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Email Templates</h1>
        <p className="text-gray-600">Customize email messages sent to users</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Template List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader><CardTitle className="text-sm">Templates</CardTitle></CardHeader>
            <CardContent className="p-2">
              {Object.entries(TEMPLATE_INFO).map(([id, info]) => {
                const template = templates.find(t => t.template_id === id);
                return (
                  <button
                    key={id}
                    onClick={() => selectTemplate(id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                      selectedTemplate === id ? 'bg-mint-100 text-mint-700' : 'hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-xl">{info.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{info.name}</p>
                      <p className="text-xs text-gray-500 truncate">{info.description}</p>
                    </div>
                    {template?.body ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                    )}
                  </button>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Editor */}
        <div className="lg:col-span-3">
          {selectedTemplate ? (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <span className="text-xl">{TEMPLATE_INFO[selectedTemplate]?.icon}</span>
                  {TEMPLATE_INFO[selectedTemplate]?.name}
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPreview(!preview)}>
                    {preview ? <><Edit3 className="w-4 h-4 mr-1" />Edit</> : <><Eye className="w-4 h-4 mr-1" />Preview</>}
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={saving} className="bg-mint-500 hover:bg-mint-600">
                    <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {preview ? (
                  <div className="border rounded-lg p-6 bg-white">
                    <div className="border-b pb-4 mb-4">
                      <p className="text-sm text-gray-500">Subject:</p>
                      <p className="font-semibold">{formData.subject || '(No subject)'}</p>
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: formData.body.replace(/\n/g, '<br/>') || '<p class="text-gray-400">No content</p>' }} />
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Subject Line</Label>
                      <Input
                        value={formData.subject}
                        onChange={(e) => setFormData(p => ({ ...p, subject: e.target.value }))}
                        placeholder="Email subject..."
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Email Body (HTML supported)</Label>
                      <textarea
                        value={formData.body}
                        onChange={(e) => setFormData(p => ({ ...p, body: e.target.value }))}
                        placeholder="Email content... Use {{name}}, {{email}}, {{link}} as placeholders"
                        className="w-full h-64 p-4 border rounded-lg font-mono text-sm resize-none"
                      />
                      <p className="text-xs text-gray-500">
                        Available placeholders: {'{{name}}'}, {'{{email}}'}, {'{{link}}'}, {'{{site_name}}'}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                      <Switch
                        checked={formData.is_active}
                        onCheckedChange={(c) => setFormData(p => ({ ...p, is_active: c }))}
                      />
                      <Label>Active (send this email)</Label>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Select a template to edit</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEmailTemplates;
